/**
 * /admin/dingtalk — 钉钉对接(园长 / 集团管理员)。
 *
 * 同步钉钉员工通讯录 + 家校通讯录(只读),然后:
 *  - 老师绑定:钉钉成员 ↔ 老师档案。同步时按手机号、邮箱自动绑定;其余手动
 *    选。绑定后该成员用钉钉登录就是这位老师本人。
 *  - 学生匹配:钉钉学生 ↔ 学生档案(同名自动匹配),家长据此只看到自己孩子。
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useDingtalkBind,
  useDingtalkStatus,
  useDingtalkStudentMatch,
  useDingtalkSync,
  useDingtalkUnbind,
  useTeacherPhone,
  type DtMember,
  type DtStudent,
  type DtSyncResult,
  type DtTeacher,
} from "@/api/dingtalk";
import { useSchoolStudents } from "@/api/school";

const BOUND_CLS: Record<string, string> = {
  auto_mobile: "bg-emerald-100 text-emerald-700",
  auto_email: "bg-emerald-100 text-emerald-700",
  auto_name: "bg-emerald-100 text-emerald-700",
  manual: "bg-sky-100 text-sky-700",
};

export default function DingtalkPage() {
  const { t } = useTranslation();
  const status = useDingtalkStatus();
  const sync = useDingtalkSync();
  const [result, setResult] = useState<DtSyncResult | null>(null);
  const [tab, setTab] = useState<"teachers" | "students" | "phones">("teachers");
  const data = status.data;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{t("dingtalk.title")}</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">{t("dingtalk.hint")}</p>
        </div>
        <div className="flex items-center gap-3">
          {data?.last_synced_at && (
            <span className="text-xs text-slate-400">
              {t("dingtalk.lastSync", { at: new Date(data.last_synced_at).toLocaleString() })}
            </span>
          )}
          <button
            onClick={async () => setResult(await sync.mutateAsync()) }
            disabled={sync.isPending || data?.configured === false}
            className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
            data-testid="dingtalk-sync"
          >
            {sync.isPending ? t("dingtalk.syncing") : t("dingtalk.sync")}
          </button>
        </div>
      </header>

      {data?.configured === false && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {t("dingtalk.notConfigured")}
        </div>
      )}
      {sync.error && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {(sync.error as Error).message}
        </div>
      )}
      {result && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {t("dingtalk.syncResult", { ...result })}
        </div>
      )}
      {status.error && <p className="text-sm text-rose-600">{(status.error as Error).message}</p>}

      {data && (
        <>
          <div className="mb-3 flex gap-1 border-b border-slate-200 text-sm">
            {(["teachers", "students", "phones"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`-mb-px border-b-2 px-3 py-2 ${tab === k ? "border-sky-500 text-sky-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              >
                {t(`dingtalk.tabs.${k}`, {
                  bound: data.members.filter((m) => m.teacher_id).length,
                  total: data.members.length,
                  matched: data.students.filter((s) => s.student_id).length,
                  students: data.students.length,
                  guardians: data.guardian_count,
                })}
              </button>
            ))}
          </div>
          {tab === "teachers" && <TeacherBinding members={data.members} teachers={data.teachers} />}
          {tab === "students" && <StudentMatching students={data.students} schoolId={data.school_id} />}
          {tab === "phones" && <TeacherPhones teachers={data.teachers} />}
        </>
      )}
    </div>
  );
}

function TeacherBinding({ members, teachers }: { members: DtMember[]; teachers: DtTeacher[] }) {
  const { t } = useTranslation();
  const bind = useDingtalkBind();
  const unbind = useDingtalkUnbind();
  const [pick, setPick] = useState<Record<string, string>>({});
  const free = teachers.filter((x) => x.has_account && !x.dingtalk_userid);

  if (members.length === 0) return <p className="py-6 text-sm text-slate-500">{t("dingtalk.emptyMembers")}</p>;
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      {(bind.error || unbind.error) && (
        <p className="px-4 py-2 text-sm text-rose-600">{((bind.error || unbind.error) as Error).message}</p>
      )}
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t("dingtalk.cols.member")}</th>
            <th className="text-left px-4 py-2 font-medium">{t("dingtalk.cols.teacher")}</th>
            <th className="px-4 py-2 w-40"></th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const chosen = pick[m.userid] ?? m.suggested_teacher_id ?? "";
            return (
              <tr key={m.userid} className="border-t border-slate-100">
                <td className="px-4 py-2.5">
                  <div className="text-slate-800">{m.name}</div>
                  <div className="text-xs text-slate-400">
                    {m.is_teacher ? t("dingtalk.isEduTeacher") : t("dingtalk.isStaff")}
                    {!m.has_mobile && ` · ${t("dingtalk.noMobile")}`}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  {m.teacher_id ? (
                    <span className="flex items-center gap-2">
                      <span className="text-slate-800">{m.teacher_name}</span>
                      {m.bound_by && (
                        <span className={`px-2 py-0.5 rounded text-xs ${BOUND_CLS[m.bound_by]}`}>
                          {t(`dingtalk.boundBy.${m.bound_by}`)}
                        </span>
                      )}
                    </span>
                  ) : !m.has_unionid ? (
                    <span className="text-xs text-slate-400">{t("dingtalk.noUnionid")}</span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <select
                        value={chosen}
                        onChange={(e) => setPick({ ...pick, [m.userid]: e.target.value })}
                        className="rounded border border-slate-200 px-2 py-1 text-sm"
                        aria-label={t("dingtalk.cols.teacher")}
                      >
                        <option value="">{t("dingtalk.pickTeacher")}</option>
                        {free.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}{x.email ? ` · ${x.email}` : ""}
                          </option>
                        ))}
                      </select>
                      {m.suggested_teacher_id && !pick[m.userid] && (
                        <span className="text-xs text-amber-600">{t("dingtalk.sameName")}</span>
                      )}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {m.teacher_id ? (
                    <button
                      onClick={() => unbind.mutate({ teacher_id: m.teacher_id! })}
                      className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      {t("dingtalk.unbind")}
                    </button>
                  ) : (
                    m.has_unionid && (
                      <button
                        onClick={() => chosen && bind.mutate({ teacher_id: chosen, dingtalk_userid: m.userid })}
                        disabled={!chosen || bind.isPending}
                        className="px-2 py-1 text-xs rounded border border-sky-200 text-sky-600 hover:bg-sky-50 disabled:opacity-40"
                      >
                        {t("dingtalk.bind")}
                      </button>
                    )
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StudentMatching({ students, schoolId }: { students: DtStudent[]; schoolId: string }) {
  const { t } = useTranslation();
  const roster = useSchoolStudents(schoolId);
  const match = useDingtalkStudentMatch();
  if (students.length === 0) return <p className="py-6 text-sm text-slate-500">{t("dingtalk.emptyStudents")}</p>;
  const options = (roster.data ?? []).filter((s) => s.status === "active");
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      {match.error && <p className="px-4 py-2 text-sm text-rose-600">{(match.error as Error).message}</p>}
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t("dingtalk.cols.dtStudent")}</th>
            <th className="text-left px-4 py-2 font-medium">{t("dingtalk.cols.guardians")}</th>
            <th className="text-left px-4 py-2 font-medium">{t("dingtalk.cols.record")}</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.userid} className="border-t border-slate-100">
              <td className="px-4 py-2.5">
                <div className="text-slate-800">{s.name}</div>
                <div className="text-xs text-slate-400">{s.class_name}</div>
              </td>
              <td className="px-4 py-2.5 text-slate-600">{s.guardians || "—"}</td>
              <td className="px-4 py-2.5">
                <span className="flex items-center gap-2">
                  <select
                    value={s.student_id ?? ""}
                    onChange={(e) => match.mutate({ dingtalk_userid: s.userid, student_id: e.target.value || null })}
                    className="rounded border border-slate-200 px-2 py-1 text-sm"
                    aria-label={t("dingtalk.cols.record")}
                  >
                    <option value="">{t("dingtalk.unmatched")}</option>
                    {options.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name_zh}{o.name_en ? ` (${o.name_en})` : ""} · {o.grade}{o.student_code ? ` · ${o.student_code}` : ""}
                      </option>
                    ))}
                  </select>
                  {s.matched_by && (
                    <span className={`px-2 py-0.5 rounded text-xs ${BOUND_CLS[s.matched_by]}`}>
                      {t(`dingtalk.boundBy.${s.matched_by}`)}
                    </span>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeacherPhones({ teachers }: { teachers: DtTeacher[] }) {
  const { t } = useTranslation();
  const save = useTeacherPhone();
  const [draft, setDraft] = useState<Record<string, string>>({});
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      <p className="px-4 py-2 text-xs text-slate-500">{t("dingtalk.phonesHint")}</p>
      {save.error && <p className="px-4 py-2 text-sm text-rose-600">{(save.error as Error).message}</p>}
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t("dingtalk.cols.teacher")}</th>
            <th className="text-left px-4 py-2 font-medium">{t("dingtalk.cols.phone")}</th>
            <th className="text-left px-4 py-2 font-medium">{t("dingtalk.cols.status")}</th>
          </tr>
        </thead>
        <tbody>
          {teachers.map((x) => {
            const v = draft[x.id] ?? x.phone ?? "";
            return (
              <tr key={x.id} className="border-t border-slate-100">
                <td className="px-4 py-2">
                  <div className="text-slate-800">{x.name}</div>
                  <div className="text-xs text-slate-400">{x.email ?? ""}</div>
                </td>
                <td className="px-4 py-2">
                  <input
                    id={`phone-${x.id}`}
                    value={v}
                    inputMode="tel"
                    onChange={(e) => setDraft({ ...draft, [x.id]: e.target.value })}
                    onBlur={() => {
                      if ((draft[x.id] ?? null) !== null && draft[x.id] !== (x.phone ?? ""))
                        save.mutate({ teacher_id: x.id, phone: draft[x.id] || null });
                    }}
                    className="w-40 rounded border border-slate-200 px-2 py-1 text-sm tabular-nums"
                    aria-label={t("dingtalk.cols.phone")}
                  />
                </td>
                <td className="px-4 py-2 text-xs text-slate-500">
                  {x.dingtalk_userid ? t("dingtalk.bound") : x.has_account ? t("dingtalk.notBound") : t("dingtalk.noAccount")}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
